import { zodResolver } from "@hookform/resolvers/zod";
import { confirmSignIn, signOut } from "aws-amplify/auth";
import { AuthError } from "aws-amplify/auth";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useWithLoading } from "./use-with-loading";

export const TwoFactorFormSchema = z.object({
  code: z.string({
    message: "コードを入力してください。",
  }),
});
export type TwoFactorFormSchemaType = z.infer<typeof TwoFactorFormSchema>;

type UseTwoFactorFormProps = {
  defaultValues: TwoFactorFormSchemaType;
  onMutation?: VoidFunction;
  onSuccess?: VoidFunction;
  onSubmitOverride?: (values: TwoFactorFormSchemaType) => Promise<void>;
};

export const useTwoFactorForm = ({
  defaultValues,
  onMutation,
  onSuccess,
  onSubmitOverride,
}: UseTwoFactorFormProps) => {
  const form = useForm<TwoFactorFormSchemaType>({
    resolver: zodResolver(TwoFactorFormSchema),
    defaultValues,
  });

  const handleSubmit = useWithLoading((values: TwoFactorFormSchemaType) => {
    onMutation?.();

    // Callers that verify the code elsewhere (e.g. the BE) must not touch the Amplify session.
    if (onSubmitOverride) {
      return onSubmitOverride(values).catch((e: unknown) => {
        form.setError("root", {
          message:
            e instanceof Error && e.message
              ? e.message
              : "エラーが発生しました。認証コードを再度入力してください。",
        });
      });
    }

    return confirmSignIn({
      challengeResponse: values.code,
    })
      .then(onSuccess)
      .catch(async (e: unknown) => {
        const errorMessage =
          e instanceof AuthError
            ? e.message
            : "エラーが発生しました。認証コードを再度入力してください。";
        form.setError("root", {
          message: errorMessage,
        });
        await signOut();
      });
  });

  return {
    form,
    handleSubmit: form.handleSubmit(handleSubmit),
    isLoading: handleSubmit.isLoading,
  };
};
